import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { 
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer 
} from 'recharts';
import { 
  TrendingUp, TrendingDown, Building2, Users, DollarSign, 
  Calendar, Activity, Database, AlertTriangle, CheckCircle
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLORS = ['#0D9488', '#3B82F6', '#8B5CF6', '#F59E0B', '#EF4444', '#10B981', '#F43F5E'];

const PlatformAnalytics = () => {
  const [loading, setLoading] = useState(true);
  const [platformData, setPlatformData] = useState(null);
  const [clinicComparison, setClinicComparison] = useState(null);
  const [revenueBreakdown, setRevenueBreakdown] = useState(null);
  const [systemHealth, setSystemHealth] = useState(null);
  const [userActivity, setUserActivity] = useState(null);

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [platform, clinics, revenue, health, activity] = await Promise.all([
        axios.get(`${API}/super-admin/analytics/platform-overview`),
        axios.get(`${API}/super-admin/analytics/clinic-comparison`),
        axios.get(`${API}/super-admin/analytics/revenue-breakdown`),
        axios.get(`${API}/super-admin/analytics/system-health`),
        axios.get(`${API}/super-admin/analytics/user-activity`)
      ]);

      setPlatformData(platform.data);
      setClinicComparison(clinics.data);
      setRevenueBreakdown(revenue.data);
      setSystemHealth(health.data);
      setUserActivity(activity.data);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin h-12 w-12 border-4 border-teal-600 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  // Prepare chart data
  const revenueChartData = revenueBreakdown?.daily_revenue 
    ? Object.entries(revenueBreakdown.daily_revenue).slice(-30).map(([date, amount]) => ({
        date: new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        revenue: amount
      }))
    : [];

  const userRoleData = userActivity?.by_role 
    ? Object.entries(userActivity.by_role).map(([role, count]) => ({
        name: role.charAt(0).toUpperCase() + role.slice(1),
        value: count
      }))
    : [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Platform Analytics</h2>
        <p className="text-gray-600 mt-1">Comprehensive insights across all clinics</p>
      </div>

      {/* Overview KPIs */}
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="border-l-4 border-l-teal-500">
          <CardHeader className="pb-3">
            <CardDescription>Active Clinics</CardDescription>
            <CardTitle className="text-3xl">{platformData?.overview.active_clinics}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-teal-600" />
              <span className="text-sm text-gray-600">
                {platformData?.overview.total_clinics} total
              </span>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-blue-500">
          <CardHeader className="pb-3">
            <CardDescription>Platform Revenue (30d)</CardDescription>
            <CardTitle className="text-3xl">
              {formatCurrency(platformData?.revenue.last_30_days || 0)}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              {platformData?.revenue.growth_percent >= 0 ? (
                <TrendingUp className="h-5 w-5 text-green-600" />
              ) : (
                <TrendingDown className="h-5 w-5 text-red-600" />
              )}
              <span className={`font-semibold ${platformData?.revenue.growth_percent >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {Math.abs(platformData?.revenue.growth_percent || 0).toFixed(1)}%
              </span>
              <span className="text-sm text-gray-600">vs previous period</span>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-purple-500">
          <CardHeader className="pb-3">
            <CardDescription>Total Members</CardDescription>
            <CardTitle className="text-3xl">{platformData?.overview.total_members}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <Users className="h-5 w-5 text-purple-600" />
              <span className="text-sm text-gray-600">
                {platformData?.overview.active_members} active
              </span>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-orange-500">
          <CardHeader className="pb-3">
            <CardDescription>Total Users</CardDescription>
            <CardTitle className="text-3xl">{userActivity?.total_users}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <Activity className="h-5 w-5 text-orange-600" />
              <span className="text-sm text-gray-600">
                {userActivity?.new_registrations_30d} new (30d)
              </span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* System Health */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-6 w-6 text-teal-600" />
            System Health
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-3 gap-6">
            <div>
              <h4 className="font-semibold text-gray-900 mb-3">Database Status</h4>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Status</span>
                  <Badge className="bg-green-600">
                    <CheckCircle className="h-3 w-3 mr-1" />
                    Healthy
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Total Records</span>
                  <span className="font-semibold">{systemHealth?.database.total_records.toLocaleString()}</span>
                </div>
              </div>
            </div>

            <div>
              <h4 className="font-semibold text-gray-900 mb-3">Activity (24h)</h4>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">New Users</span>
                  <span className="font-semibold">{systemHealth?.recent_activity_24h.new_users}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">New Members</span>
                  <span className="font-semibold">{systemHealth?.recent_activity_24h.new_members}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Appointments</span>
                  <span className="font-semibold">{systemHealth?.recent_activity_24h.new_appointments}</span>
                </div>
              </div>
            </div>

            <div>
              <h4 className="font-semibold text-gray-900 mb-3">Alerts</h4>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Failed Payments</span>
                  <Badge className={systemHealth?.alerts.failed_payments > 0 ? 'bg-red-600' : 'bg-gray-600'}>
                    {systemHealth?.alerts.failed_payments}
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Cancelled Appointments</span>
                  <Badge className={systemHealth?.alerts.cancelled_appointments > 0 ? 'bg-yellow-600' : 'bg-gray-600'}>
                    {systemHealth?.alerts.cancelled_appointments}
                  </Badge>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Revenue Trend */}
      <Card>
        <CardHeader>
          <CardTitle>Revenue Trend (Last 30 Days)</CardTitle>
          <CardDescription>Daily platform revenue across all clinics</CardDescription>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={revenueChartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip formatter={(value) => formatCurrency(value)} />
              <Legend />
              <Line type="monotone" dataKey="revenue" stroke="#0D9488" strokeWidth={2} name="Revenue" />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Clinic Comparison */}
      <Card>
        <CardHeader>
          <CardTitle>Clinic Performance Comparison</CardTitle>
          <CardDescription>Top performing clinics by revenue (last 30 days)</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="rounded-lg border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead>Rank</TableHead>
                  <TableHead>Clinic Name</TableHead>
                  <TableHead>Tier</TableHead>
                  <TableHead>Members</TableHead>
                  <TableHead>Revenue (30d)</TableHead>
                  <TableHead>Appointments</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {clinicComparison?.clinics.map((clinic, index) => (
                  <TableRow key={clinic.clinic_id} className="hover:bg-gray-50">
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold">#{index + 1}</span>
                        {index === 0 && <span className="text-yellow-500">🏆</span>}
                      </div>
                    </TableCell>
                    <TableCell className="font-medium">{clinic.clinic_name}</TableCell>
                    <TableCell>
                      <Badge className="bg-teal-600">{clinic.subscription_tier}</Badge>
                    </TableCell>
                    <TableCell>
                      <div className="text-center">
                        <div className="font-semibold">{clinic.active_members}</div>
                        <div className="text-xs text-gray-500">of {clinic.total_members}</div>
                      </div>
                    </TableCell>
                    <TableCell className="font-semibold text-green-600">
                      {formatCurrency(clinic.revenue_30d)}
                    </TableCell>
                    <TableCell className="text-center">{clinic.appointments_30d}</TableCell>
                    <TableCell>
                      <Badge className={clinic.status === 'active' ? 'bg-green-600' : 'bg-gray-600'}>
                        {clinic.status}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Top Revenue Clinics & User Distribution */}
      <div className="grid md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Top Revenue Clinics</CardTitle>
            <CardDescription>Last 90 days</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={revenueBreakdown?.top_clinics || []}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="clinic_name" angle={-45} textAnchor="end" height={100} />
                <YAxis />
                <Tooltip formatter={(value) => formatCurrency(value)} />
                <Bar dataKey="revenue" fill="#0D9488" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>User Distribution by Role</CardTitle>
            <CardDescription>Platform-wide user breakdown</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={userRoleData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={(entry) => `${entry.name}: ${entry.value}`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {userRoleData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PlatformAnalytics;
